import type {
  ArithmeticOperation,
  WorksheetMathematicsModuleType,
  WorksheetModule,
  WorksheetTheme,
} from "@arbeidsark/shared"
import {
  illegalMathematicsGradeTaskTypesLookup,
  legalGradeArithmeticOperationLookup,
} from "@arbeidsark/shared"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Document, Page } from "react-pdf"
import { Button, Form, FormField, Input, Label, SVGIcon } from "~/components"
import NumberInput from "~/components/Input/NumberInput"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { trpc } from "~/utils/trpc"
import { validateNonEmpty } from "~/utils/validation"
import {
  captureWorksheetConfigChange,
  captureWorksheetDownload,
  captureWorksheetSaved,
} from "../analytics"
import {
  FloatingActions,
  GeneratorWrapper,
  PdfScrollArea,
  PreviewArea,
  Sidebar,
  SidebarContent,
} from "../components/GeneratorLayout"
import GeneratorMainFrame from "../components/GeneratorMainFrame"
import MathematicsPageConfigurationList from "../components/pageConfigurations/mathematics/MathematicsPageConfigurationList"
import GradeSelect from "../components/selects/GradeSelect"
import LayoutSelect from "../components/selects/LayoutSelect"
import MathematicsTaskTypesMultiSelect from "../components/selects/MathematicsTaskTypesMultiSelect"
import OperationSelect from "../components/selects/OperationSelect"
import ThemeSelect from "../components/selects/ThemeSelect"
import { useGeneratorContext } from "../context/GeneratorContext"
import { downloadWorksheet, normalizePreviewPages } from "../util"

export type MathematicsGeneratorProps = {}

export interface MathematicsGeneratorFormData {
  name: string
  grade: number
  operation: ArithmeticOperation
  layout: "half" | "full"
  taskTypes: WorksheetMathematicsModuleType[]
  pages: number
  theme: WorksheetTheme
  moduleConfigurations: {
    [page: string]: {
      [moduleNum: string]: WorksheetModule
    }
  }
}

const MathematicsGenerator: React.FC<MathematicsGeneratorProps> = () => {
  const { worksheetName, setWorksheetName } = useGeneratorContext()

  const translations = useTranslate({
    name: "common.name",
    grade: "common.school-grade",
    operation: "common.operation",
    generate: "worksheets.buttons.generate-worksheet",
    regenerate: "worksheets.buttons.regenerate",
    save: "common.save",
    download: "common.download",
    layout: "worksheets.labels.page-layout",
    taskTypes: "worksheets.labels.select-task-types",
    numberOfPages: "worksheets.labels.number-of-pages",
    theme: "worksheets.labels.select-theme",

    newWorksheet: "common.new-worksheet",

    loading: "common.loading...",
    errors: {
      nonEmpty: "errors.generic-not-empty-field",
    },
    warnings: {
      arithmeticOperationMightNotBeSupportedForGrade:
        "warnings.worksheets.arithmetic-operation-might-not-be-supported-for-grade",
      taskTypeNotSupportedForGrade: [
        "warnings.worksheets.task-type-not-supported-for-grade",
        { taskType: "" },
      ],
    },
    taskTypeEntries: {
      "mathematics/neighbourNumbers":
        "worksheets.task-types.mathematics.neighbourNumbers",
      "mathematics/arithmetic": "worksheets.task-types.mathematics.arithmetic",
      "mathematics/sudoku": "worksheets.task-types.mathematics.sudoku",
      "mathematics/guessTheHouse":
        "worksheets.task-types.mathematics.guessTheHouse",
      "mathematics/numberPatterns":
        "worksheets.task-types.mathematics.numberPatterns",
      "mathematics/numberSeries":
        "worksheets.task-types.mathematics.numberSeries",
      "mathematics/grid": "worksheets.task-types.mathematics.grid",
      "mathematics/sorting": "worksheets.task-types.mathematics.sorting",
      "mathematics/pyramid": "worksheets.task-types.mathematics.pyramid",
      "mathematics/standardAlgorithm":
        "worksheets.task-types.mathematics.standardAlgorithm",
      "mathematics/mathCrossBox":
        "worksheets.task-types.mathematics.mathCrossBox",
    },
  })

  const {
    formValues,
    formChangeHandler,
    formErrors,
    submitHandler,
    updateForm,
    formValid,
  } = useForm<MathematicsGeneratorFormData>({
    values: {
      name: worksheetName,
      grade: 1,
      operation: "addition",
      layout: "half",
      taskTypes: [],
      pages: 2,
      theme: "numberchars",
      moduleConfigurations: {},
    },
    validators: {
      name: validateNonEmpty(translations.errors.nonEmpty),
    },
  })

  // Sync name changes to the shared context
  useEffect(() => {
    setWorksheetName(formValues.name)
  }, [formValues.name, setWorksheetName])

  const warnings = useMemo<
    Record<keyof MathematicsGeneratorFormData, string | null>
  >(() => {
    const legalArithmeticOperationsForGrade =
      legalGradeArithmeticOperationLookup[formValues.grade]

    const operationWarning = !legalArithmeticOperationsForGrade.includes(
      formValues.operation,
    )
      ? translations.warnings.arithmeticOperationMightNotBeSupportedForGrade
      : null

    const illegalTaskTypeForGrade = illegalMathematicsGradeTaskTypesLookup[
      formValues.grade
    ].find((taskType) => formValues.taskTypes.includes(taskType))
    const taskTypeWarning = illegalTaskTypeForGrade
      ? translations.warnings.taskTypeNotSupportedForGrade({
          taskType: translations.taskTypeEntries[illegalTaskTypeForGrade],
        })
      : null

    return {
      name: null,
      grade: null,
      operation: operationWarning,
      layout: null,
      taskTypes: taskTypeWarning,
      pages: null,
      theme: null,
      moduleConfigurations: null,
    }
  }, [formValues, translations])

  const toast = useToast()
  const { getToastInfo } = useErrorHandling()

  const [resolvedModules, setResolvedModules] = useState<
    WorksheetModule[][] | null
  >(null)
  const [pdfData, setPdfData] = useState<string | null>(null)
  const [pdfNumPages, setPdfNumPages] = useState<number>(0)
  const [seed, setSeed] = useState<string | null>(null)
  const [hasInitialGenerated, setHasInitialGenerated] = useState(false)

  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollPosRef = useRef<number>(0)

  const { mutate: createWorksheet, isPending: isSaving } =
    trpc.worksheets.createV2.useMutation({
      onSuccess(input) {
        captureWorksheetSaved({
          subject: "MATHEMATICS",
          grade: formValues.grade,
          worksheet_id: input.id,
          source: "generator_save",
        })
        downloadWorksheet(input.id, input.name + ".pdf", {
          subject: "MATHEMATICS",
          grade: formValues.grade,
          source: "generator_save",
        })
        toast("success", "Worksheet saved successfully")
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )
        toast(type, message)
      },
    })

  const { mutate: previewWorksheet, isPending: isPreviewLoading } =
    trpc.worksheets.previewV2.useMutation({
      onSuccess(data) {
        setResolvedModules(normalizePreviewPages(data.pages))
        setPdfData(data.pdf)
        setSeed(data.seed)
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )
        toast(type, message)
      },
    })

  const mergedConfiguration = useMemo(() => {
    const resolvedAsRecord: Record<string, Record<string, WorksheetModule>> = {}
    if (resolvedModules) {
      resolvedModules.forEach((page, pageIndex) => {
        resolvedAsRecord[pageIndex] = {}
        page.forEach((module, moduleIndex) => {
          resolvedAsRecord[pageIndex][moduleIndex] = module
        })
      })
    }

    const merged: Record<string, Record<string, WorksheetModule>> = {
      ...resolvedAsRecord,
    }
    Object.keys(formValues.moduleConfigurations).forEach((pageKey) => {
      merged[pageKey] = {
        ...merged[pageKey],
        ...formValues.moduleConfigurations[pageKey],
      }
    })

    return merged
  }, [resolvedModules, formValues.moduleConfigurations])

  const onSubmit = useCallback(() => {
    if (!formValid) {
      return
    }
    // Clear seed on new generation if it's a full regenerate
    // But for now, let's assume the button generates new random sheet if no seed is passed,
    // OR if we want to keep the seed we must pass it.
    // The user usually expects "Generate" to make a NEW worksheet.
    // So we don't pass the seed here.
    previewWorksheet({
      subject: "MATHEMATICS",
      name: formValues.name,
      grade: formValues.grade,
      theme: formValues.theme,
      pages: formValues.pages,
      operation: formValues.operation,
      sizeConfiguration: formValues.layout,
      allowedModuleTypes:
        formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
      moduleConfiguration: formValues.moduleConfigurations as any,
    })
  }, [previewWorksheet, formValues, formValid])

  // Initial generation
  useEffect(() => {
    if (!hasInitialGenerated) {
      onSubmit()
      setHasInitialGenerated(true)
    }
  }, [hasInitialGenerated, onSubmit])

  // Auto-regenerate when page count changes
  const previousPagesRef = useRef(formValues.pages)
  useEffect(() => {
    if (hasInitialGenerated && formValues.pages !== previousPagesRef.current) {
      previousPagesRef.current = formValues.pages
      onSubmit()
    }
  }, [formValues.pages, hasInitialGenerated, onSubmit])

  const onRegenerateModule = useCallback(
    (pageIndex: number, moduleIndex: number) => {
      if (!seed) return

      if (scrollAreaRef.current) {
        scrollPosRef.current = scrollAreaRef.current.scrollTop
      }

      // To regenerate a specific module, we update its configuration with a new seed/salt
      // We need to preserve the global seed so other modules stay the same.
      const moduleSeed = Math.random().toString(36).substring(7)

      const currentModuleConfig =
        formValues.moduleConfigurations[pageIndex]?.[moduleIndex]

      // We need to update the form with the new seed for this module
      // But wait, if we update the form, we "lock" this module type as well if it wasn't locked.
      // If it was random, we want to keep it random type but new content?
      // Or just re-roll content?
      // If we set a seed on the module, we lock its content generation.

      // If the module type is currently "random" (not in formValues), we should probably
      // lock it to the type it resolved to, OR just let it re-roll type too?
      // The user request: "regenerate a specific module but LET THE REST OF THE document stay the same"
      // usually implies "I don't like this specific task, give me another one of the SAME type".

      // So we should lock the type to what it is currently resolved to, and give it a new seed.
      const resolvedModule = resolvedModules?.[pageIndex]?.[moduleIndex]
      if (!resolvedModule) return

      // Create a new configuration map that includes ALL resolved modules
      // This ensures we "freeze" the state of all other modules
      const newModuleConfigurations: Record<
        string,
        Record<string, WorksheetModule>
      > = {
        ...formValues.moduleConfigurations,
      }

      // Populate with resolved modules if they aren't already in formValues
      resolvedModules?.forEach((page, pIdx) => {
        if (!newModuleConfigurations[pIdx]) {
          newModuleConfigurations[pIdx] = {}
        }
        page.forEach((mod, mIdx) => {
          // If we don't have an explicit config for this module, lock it to the resolved state
          if (!newModuleConfigurations[pIdx][mIdx]) {
            newModuleConfigurations[pIdx][mIdx] = mod
          }
        })
      })

      // Update the specific module we are regenerating
      newModuleConfigurations[pageIndex][moduleIndex] = {
        ...resolvedModule,
        ...currentModuleConfig,
        seed: moduleSeed,
      } as any

      updateForm({
        moduleConfigurations: newModuleConfigurations,
      })

      // Then trigger preview with the SAME global seed
      previewWorksheet({
        subject: "MATHEMATICS",
        name: formValues.name,
        grade: formValues.grade,
        theme: formValues.theme,
        pages: formValues.pages,
        operation: formValues.operation,
        sizeConfiguration: formValues.layout,
        allowedModuleTypes:
          formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
        moduleConfiguration: newModuleConfigurations as any,
        seed: seed, // Important: Keep the global seed!
      })
    },
    [seed, formValues, resolvedModules, updateForm, previewWorksheet],
  )

  const onSave = useCallback(() => {
    if (!formValid) {
      return
    }

    // We want to save EXACTLY what is shown.
    createWorksheet({
      subject: "MATHEMATICS",
      name: formValues.name,
      grade: formValues.grade,
      theme: formValues.theme,
      pages: formValues.pages,
      operation: formValues.operation,
      sizeConfiguration: formValues.layout,
      allowedModuleTypes:
        formValues.taskTypes.length > 0 ? formValues.taskTypes : undefined,
      moduleConfiguration: mergedConfiguration as any,
      seed: seed || undefined,
    })
  }, [createWorksheet, formValues, formValid, mergedConfiguration, seed])

  const onGradeChange = useCallback(
    (value?: number | null) => {
      if (!value) {
        return
      }

      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: value,
        },
        "grade",
        formValues.grade,
        value,
      )

      updateForm({
        grade: value,
      })
    },
    [formValues.grade, updateForm],
  )

  const onOperationChange = useCallback(
    (value: ArithmeticOperation) => {
      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: formValues.grade,
        },
        "operation",
        formValues.operation,
        value,
      )
      formChangeHandler("operation")(value)
    },
    [formValues.grade, formValues.operation, formChangeHandler],
  )

  const onLayoutChange = useCallback(
    (value: "half" | "full") => {
      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: formValues.grade,
        },
        "size",
        formValues.layout,
        value,
      )
      updateForm({
        layout: value,
      })
    },
    [formValues.grade, formValues.layout, updateForm],
  )

  const onTaskTypesChange = useCallback(
    (value: WorksheetMathematicsModuleType[]) => {
      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: formValues.grade,
        },
        "difficulty",
        formValues.taskTypes,
        value,
      )
      formChangeHandler("taskTypes")(value)
    },
    [formValues.grade, formValues.taskTypes, formChangeHandler],
  )

  const onPagesChange = useCallback(
    (value: number) => {
      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: formValues.grade,
        },
        "pages",
        formValues.pages,
        value,
      )
      formChangeHandler("pages")(value)
    },
    [formValues.grade, formValues.pages, formChangeHandler],
  )

  const onThemeChange = useCallback(
    (value: WorksheetTheme) => {
      captureWorksheetConfigChange(
        {
          subject: "MATHEMATICS",
          grade: formValues.grade,
        },
        "theme",
        formValues.theme,
        value,
      )
      formChangeHandler("theme")(value)
    },
    [formValues.grade, formValues.theme, formChangeHandler],
  )

  const downloadPdf = useCallback((name: string, base64data: string) => {
    const linkSource = `data:application/pdf;base64,${base64data}`
    const downloadLink = document.createElement("a")
    const fileName = name

    downloadLink.href = linkSource
    downloadLink.download = fileName
    downloadLink.click()
  }, [])

  return (
    <GeneratorWrapper>
      <Sidebar>
        <GeneratorMainFrame basePath="/generator" />
        <Form
          preventDefault
          onSubmit={submitHandler(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden min-h-0"
        >
          <SidebarContent data-tour-scroll-container>
            <div data-tour="general-settings">
              <FormField>
                <Label>{translations.name}</Label>
                <Input
                  value={formValues.name}
                  error={formErrors.name}
                  onChange={formChangeHandler("name")}
                  width="100%"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                    }
                  }}
                />
              </FormField>
              <FormField>
                <Label>{translations.grade}</Label>
                <GradeSelect
                  value={formValues.grade}
                  onChange={onGradeChange}
                />
              </FormField>
              <FormField>
                <Label>{translations.operation}</Label>
                <OperationSelect
                  value={formValues.operation}
                  onChange={onOperationChange}
                  warning={warnings.operation}
                />
              </FormField>
              <FormField>
                <Label>{translations.layout}</Label>
                <LayoutSelect
                  value={formValues.layout}
                  onChange={onLayoutChange}
                />
              </FormField>
              <FormField>
                <Label>{translations.taskTypes}</Label>
                <MathematicsTaskTypesMultiSelect
                  value={formValues.taskTypes}
                  onChange={onTaskTypesChange}
                  width="100%"
                  warning={warnings.taskTypes}
                />
              </FormField>
              <FormField data-tour="pages-input">
                <Label>{translations.numberOfPages}</Label>
                <NumberInput
                  value={formValues.pages}
                  onChange={onPagesChange}
                  minValue={1}
                  maxValue={10}
                  width="100%"
                  showPlusMinusButtons={true}
                />
              </FormField>
              <FormField>
                <Label>{translations.theme}</Label>
                <ThemeSelect
                  value={formValues.theme}
                  onChange={onThemeChange}
                />
              </FormField>
              <FormField>
                <span data-tour="global-regenerate-button">
                  <Button
                    width="100%"
                    disabled={isPreviewLoading || !formValid}
                  >
                    {isPreviewLoading
                      ? translations.loading
                      : translations.regenerate}
                  </Button>
                </span>
              </FormField>
            </div>

            {pdfData && (
              <div data-tour="page-configurations">
                <MathematicsPageConfigurationList
                  numberOfPages={formValues.pages}
                  layout={formValues.layout}
                  pagesConfiguration={mergedConfiguration as any}
                  onPageModuleConfigurationChanged={(
                    page,
                    moduleNumber,
                    moduleConfiguration,
                  ) => {
                    updateForm({
                      moduleConfigurations: {
                        ...formValues.moduleConfigurations,
                        [page]: {
                          ...formValues.moduleConfigurations[page],
                          [moduleNumber]: moduleConfiguration,
                        },
                      },
                    })
                  }}
                  onRegenerateModule={onRegenerateModule}
                />
              </div>
            )}
          </SidebarContent>
        </Form>
      </Sidebar>

      <PreviewArea data-tour="preview-area">
        {pdfData && (
          <>
            <FloatingActions>
              <span data-tour="download-button">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    if (pdfData) {
                      captureWorksheetDownload({
                        subject: "MATHEMATICS",
                        grade: formValues.grade,
                        source: "generator_preview",
                      })
                      downloadPdf(formValues.name + ".pdf", pdfData)
                    }
                  }}
                  title={translations.download}
                >
                  <SVGIcon icon="Download" className="w-4 h-4" />
                </Button>
              </span>
              <span data-tour="save-button">
                <Button
                  size="small"
                  variant="secondary"
                  disabled={isSaving || !formValid}
                  onClick={onSave}
                  title={translations.save}
                >
                  {isSaving ? (
                    translations.loading
                  ) : (
                    <>
                      <SVGIcon icon="Check" className="w-4 h-4 mr-2" />
                      {translations.save}
                    </>
                  )}
                </Button>
              </span>
            </FloatingActions>
            <PdfScrollArea ref={scrollAreaRef}>
              <Document
                file={`data:application/pdf;base64,${pdfData}`}
                className="flex flex-col gap-4"
                onLoadSuccess={({ numPages }) => {
                  setPdfNumPages(numPages)
                  if (scrollAreaRef.current) {
                    scrollAreaRef.current.scrollTop = scrollPosRef.current
                  }
                }}
              >
                {Array.from(new Array(pdfNumPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={600}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => {
                      if (index === 0 && scrollAreaRef.current) {
                        scrollAreaRef.current.scrollTop = scrollPosRef.current
                      }
                    }}
                  />
                ))}
              </Document>
            </PdfScrollArea>
          </>
        )}
        {!pdfData && isPreviewLoading && (
          <div className="flex items-center justify-center h-full text-gray-400">
            {translations.loading}
          </div>
        )}
      </PreviewArea>
    </GeneratorWrapper>
  )
}

export default MathematicsGenerator
